/**
 * Where the week stands, for anyone on a schedule.
 *
 * Filled means it happened — accent for a session done, warning for one missed,
 * a ringed accent dot for an extra session the plan did not ask for. Hollow
 * means not yet. A dash is a rest day, which is the absence of a schedule entry
 * rather than anything the schedule says.
 *
 * It tops every plan frame because the bolt is what a user opens on a training
 * day, so it should answer "where am I this week" without them closing it.
 */

import { Fragment } from 'react';

import {
  START_WORKOUT_COPY,
  dayLabel,
  dayLetter,
  ribbonDotStatus,
  weekTallyLines,
} from '@personal-trainer-app/shared/src/utils/workout/start-options';

import type { WeekRibbonProps } from './StartWorkoutSheet.types';
import type { StartOptionsDay } from '@personal-trainer-app/shared/src/types/domain/start-options.types';

import { CopyRuns } from '@/components/ui/CopyRuns';
import { Text } from '@/components/ui/primitives';

/**
 * One class per status, complete — never a shared base plus an override. Two
 * `border-*` classes on one element resolve by stylesheet order, not by the
 * order they are written, so the ring would silently draw in the wrong colour.
 */
const DOT_BY_STATUS: Record<StartOptionsDay['status'], string> = {
  done: 'h-2.5 w-2.5 rounded-full border-[1.5px] border-primary-400 bg-primary-400',
  missed:
    'h-2.5 w-2.5 rounded-full border-[1.5px] border-warning-400 bg-warning-400',
  due: 'h-2.5 w-2.5 rounded-full border-[1.5px] border-stroke-strong',
  rest: 'h-0.5 w-2 rounded-sm bg-neutral-700',
};

export function WeekRibbon({
  days,
  today,
}: WeekRibbonProps): React.JSX.Element {
  return (
    <div
      className="mb-3.5 flex items-center justify-between gap-3 rounded-lg border border-stroke-subtle bg-contrast-700 px-3 py-2.5"
      data-testid="start-workout-week"
    >
      <ul className="flex gap-[3px]" aria-label={START_WORKOUT_COPY.weekLabel}>
        {days.map((day) => {
          const isToday = day.date === today;

          return (
            <li
              key={day.date}
              className="relative flex h-[34px] w-6 flex-col items-center justify-end gap-1.5"
            >
              {isToday && (
                <span
                  aria-hidden="true"
                  className="absolute -inset-y-[3px] -inset-x-0.5 rounded-md border border-stroke-accent bg-sakura-wash"
                />
              )}

              <span
                aria-hidden="true"
                className={`relative block ${DOT_BY_STATUS[ribbonDotStatus(day)]} ${
                  day.extra > 0
                    ? 'ring-1 ring-stroke-accent-strong ring-offset-2 ring-offset-contrast-700'
                    : ''
                }`}
              />

              <Text
                as="span"
                variant="label-xs"
                color={isToday ? 'accent' : 'muted'}
                className="relative font-semibold leading-none"
              >
                <span aria-hidden="true">{dayLetter(day)}</span>
                <span className="sr-only">{dayLabel(day)}</span>
              </Text>
            </li>
          );
        })}
      </ul>

      <Text
        as="div"
        variant="body-sm"
        color="secondary"
        className="flex-none text-right leading-snug"
      >
        {weekTallyLines(days).map((line, index) => (
          <Fragment key={index}>
            {index > 0 && <br />}
            <CopyRuns
              segments={line}
              figureClassName="font-mono font-semibold tabular-nums text-neutral-100"
            />
          </Fragment>
        ))}
      </Text>
    </div>
  );
}

export default WeekRibbon;
